/**
 * WatermarkTablesTable Component
 *
 * Displays a sortable table of tables with watermark configuration status
 */

import { useState, useMemo } from 'react';
import { WatermarkTable, SyncFrequency } from '../../lib/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { ArrowUpDown, Settings, Clock, Calendar } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface WatermarkTablesTableProps {
  tables: WatermarkTable[];
  onTableSelect: (tableName: string) => void;
  selectedRows: Set<string>;
  onRowSelect: (tableName: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  onSyncFrequencyChange?: (tableName: string, newFrequency: SyncFrequency) => void;
}

type SortField = 'tableName' | 'rowCount' | 'oracleRowCount' | 'azureRowCount' | 'lastSyncDate' | 'hasWatermark';
type SortDirection = 'asc' | 'desc';

export function WatermarkTablesTable({
  tables,
  onTableSelect,
  selectedRows,
  onRowSelect,
  onSelectAll,
  onSyncFrequencyChange
}: WatermarkTablesTableProps) {
  const [sortField, setSortField] = useState<SortField>('rowCount');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Sort tables
  const sortedTables = useMemo(() => {
    return [...tables].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'tableName':
          comparison = a.tableName.localeCompare(b.tableName);
          break;
        case 'rowCount':
          comparison = (a.rowCount || 0) - (b.rowCount || 0);
          break;
        case 'oracleRowCount':
          comparison = (a.oracleRowCount || 0) - (b.oracleRowCount || 0);
          break;
        case 'azureRowCount':
          comparison = (a.azureRowCount || 0) - (b.azureRowCount || 0);
          break;
        case 'lastSyncDate':
          const dateA = a.lastSyncDate ? new Date(a.lastSyncDate).getTime() : 0;
          const dateB = b.lastSyncDate ? new Date(b.lastSyncDate).getTime() : 0;
          comparison = dateA - dateB;
          break;
        case 'hasWatermark':
          comparison = (a.hasWatermark ? 1 : 0) - (b.hasWatermark ? 1 : 0);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [tables, sortField, sortDirection]);

  // Check if all rows are selected
  const allSelected = useMemo(() => {
    return sortedTables.length > 0 && sortedTables.every(t => selectedRows.has(t.tableName));
  }, [sortedTables, selectedRows]);

  const someSelected = useMemo(() => {
    return sortedTables.some(t => selectedRows.has(t.tableName));
  }, [sortedTables, selectedRows]);

  // Format row count with commas
  const formatRowCount = (count: number) => {
    return count.toLocaleString();
  };

  // Format last sync date
  const formatLastSync = (dateString: string | null) => {
    if (!dateString) return 'Never';

    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">
              <Checkbox
                checked={allSelected}
                onCheckedChange={(checked) => onSelectAll(checked as boolean)}
                aria-label="Select all"
              />
            </TableHead>
            <TableHead>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSort('tableName')}
                className="hover:bg-transparent"
              >
                Table Name
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
            </TableHead>
            <TableHead>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSort('oracleRowCount')}
                className="hover:bg-transparent"
              >
                Oracle Rows
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
            </TableHead>
            <TableHead>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSort('azureRowCount')}
                className="hover:bg-transparent"
              >
                Azure Rows
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
            </TableHead>
            <TableHead>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSort('rowCount')}
                className="hover:bg-transparent"
              >
                Last Sync Rows
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
            </TableHead>
            <TableHead>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSort('lastSyncDate')}
                className="hover:bg-transparent"
              >
                Last Sync
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </Button>
            </TableHead>
            <TableHead>Sync Tier</TableHead>
            <TableHead>Frequency</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedTables.map((table) => {
            const isSelected = selectedRows.has(table.tableName);
            return (
              <TableRow
                key={table.tableName}
                className={`cursor-pointer hover:bg-muted/50 ${isSelected ? 'bg-muted/30' : ''}`}
                onClick={() => onTableSelect(table.tableName)}
              >
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={(checked) => onRowSelect(table.tableName, checked as boolean)}
                    aria-label={`Select ${table.tableName}`}
                  />
                </TableCell>
                <TableCell className="font-mono text-sm">{table.tableName}</TableCell>
              <TableCell className="text-right font-mono text-sm">
                {table.oracleRowCount != null ? formatRowCount(table.oracleRowCount) : <span className="text-muted-foreground">-</span>}
              </TableCell>
              <TableCell className="text-right font-mono text-sm">
                {table.azureRowCount != null ? formatRowCount(table.azureRowCount) : <span className="text-muted-foreground">-</span>}
              </TableCell>
              <TableCell className="text-right font-mono text-sm">{formatRowCount(table.rowCount)}</TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatLastSync(table.lastSyncDate)}
              </TableCell>
              <TableCell>
                {table.syncTier ? (
                  <Badge
                    variant="outline"
                    className={
                      table.syncTier === 'CDC' ? 'bg-green-50 text-green-700 border-green-200' :
                      table.syncTier === 'INCREMENTAL' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                      table.syncTier.startsWith('FULL_') ? 'bg-orange-50 text-orange-700 border-orange-200' :
                      'bg-gray-50 text-gray-700 border-gray-200'
                    }
                  >
                    {table.syncTier}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-yellow-300 text-yellow-700">
                    Unknown
                  </Badge>
                )}
              </TableCell>
              <TableCell onClick={(e) => e.stopPropagation()}>
                {table.syncFrequency ? (
                  <Badge
                    variant="outline"
                    className={`cursor-pointer transition-colors ${
                      table.syncFrequency === 'HOURLY'
                        ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                        : 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'
                    }`}
                    onClick={() => {
                      if (onSyncFrequencyChange) {
                        const newFrequency: SyncFrequency = table.syncFrequency === 'HOURLY' ? 'DAILY' : 'HOURLY';
                        onSyncFrequencyChange(table.tableName, newFrequency);
                      }
                    }}
                  >
                    {table.syncFrequency === 'HOURLY' ? (
                      <Clock className="h-3 w-3 mr-1" />
                    ) : (
                      <Calendar className="h-3 w-3 mr-1" />
                    )}
                    {table.syncFrequency}
                  </Badge>
                ) : (
                  <span className="text-muted-foreground text-sm">-</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    onTableSelect(table.tableName);
                  }}
                >
                  <Settings className="h-4 w-4 mr-1" />
                  Configure
                </Button>
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
